import React, { useState, useEffect, useMemo } from 'react';
import {
  WhatsAppTemplate,
  WhatsAppTemplateType,
  WhatsAppTemplateScope,
  WhatsAppTemplateCategory,
  WhatsAppTemplateCreate,
  WhatsAppButton,
  WhatsAppHeaderFormat,
  CarouselCard,
  Asset,
  TemplateParams,
} from '../../types';
import {
  createWhatsAppTemplate, deleteWhatsAppTemplate, patchWhatsAppTemplate, submitWhatsAppTemplate, updateWhatsAppTemplate,
} from '../../services/marketingApi';
import WhatsAppBubble from './WhatsAppBubble';
import AssetPickerModal from './AssetPickerModal';
import ButtonEditor from './ButtonEditor';
import { blanksOf, render, statusTone } from './shape';
import { Modal, Pill, Tabs, Thumb, btn, btnTone, input, label } from '../../components/Kit';
import { testSend } from '../../services/coreApi';

/** The one template form: view = edit = create. A row is a SHAPE (its own
 *  body and Meta status) or FILLS a shape (`parent_id` + `params`, the
 *  parent's status inherited) — the mode switch at the top. Once a shape is
 *  submitted to Meta its shape fields lock; what the grid and the pickers see
 *  (name, group, best for, header media, tags) stays editable and goes out
 *  as a PATCH. Nothing here sends a message. */

interface TemplateFormProps {
  template: WhatsAppTemplate | null; // null = create mode, object = edit mode
  shapes: WhatsAppTemplate[]; // rows a filling row may fill (lead-facing shapes)
  onClose: () => void;
  onSuccess: () => void;
}

type Mode = 'shape' | 'fill';

const TEMPLATE_TYPES: { value: WhatsAppTemplateType; label: string }[] = [
  { value: 'TEXT', label: 'Text' },
  { value: 'TEXT_HEADER', label: 'Text Header' },
  { value: 'IMAGE', label: 'Image' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'Document' },
  { value: 'CAROUSEL', label: 'Carousel' },
];

const CATEGORIES: { value: WhatsAppTemplateCategory; label: string }[] = [
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'UTILITY', label: 'Utility' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'mr', label: 'Marathi' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'bn', label: 'Bengali' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'kn', label: 'Kannada' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'pa', label: 'Punjabi' },
];

/**
 * Detect template variables ({{1}}, {{2}}, etc.) from text
 */
const detectVariables = (text: string): string[] => {
  const matches = text.match(/\{\{(\d+)\}\}/g) || [];
  const unique = Array.from(new Set(matches)).sort();
  return unique.map((v) => v.replace(/[{}]/g, ''));
};

/**
 * Get header format based on template type
 */
const getHeaderFormatForType = (type: WhatsAppTemplateType): WhatsAppHeaderFormat => {
  switch (type) {
    case 'TEXT':
      return 'NONE';
    case 'TEXT_HEADER':
      return 'TEXT';
    case 'IMAGE':
      return 'IMAGE';
    case 'VIDEO':
      return 'VIDEO';
    case 'DOCUMENT':
      return 'DOCUMENT';
    case 'CAROUSEL':
      return 'NONE';
    default:
      return 'NONE';
  }
};

const TemplateForm: React.FC<TemplateFormProps> = ({ template, shapes, onClose, onSuccess }) => {
  // Form fields - basic info
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [templateType, setTemplateType] = useState<WhatsAppTemplateType>('TEXT');
  const [templateScope, setTemplateScope] = useState<WhatsAppTemplateScope>('IN_SESSION');
  const [category, setCategory] = useState<WhatsAppTemplateCategory>('MARKETING');
  const [language, setLanguage] = useState('en');

  // Header fields
  const [headerText, setHeaderText] = useState('');
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');

  // Body fields
  const [body, setBody] = useState('');
  const [bodyVariables, setBodyVariables] = useState<string[]>([]);

  // Footer
  const [footer, setFooter] = useState('');

  // Buttons (for non-carousel)
  const [buttonType, setButtonType] = useState<'quick_reply' | 'cta' | null>(null);
  const [buttons, setButtons] = useState<WhatsAppButton[]>([]);

  // Carousel cards
  const [carouselCards, setCarouselCards] = useState<CarouselCard[]>([]);
  const [expandedCardIndex, setExpandedCardIndex] = useState<number>(0);

  // Tags
  const [tags, setTags] = useState('');

  // Shape or filling row; the filling row's blanks
  const [mode, setMode] = useState<Mode>(template?.parent_id ? 'fill' : 'shape');
  const [parentId, setParentId] = useState(template?.parent_id || '');
  const [bodyParams, setBodyParams] = useState<string[]>(template?.params?.body_params || []);
  const [fillHeaderUrl, setFillHeaderUrl] = useState(template?.params?.header_media_url || '');
  const [linkParams, setLinkParams] = useState(template?.params?.link_params || '');

  // Curation — what the grid, the pickers and the numbers see; always editable
  const [group, setGroup] = useState(template?.group || '');
  const [bestFor, setBestFor] = useState(template?.best_for || '');
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [tested, setTested] = useState<string | null>(null);

  // A shape submitted to Meta keeps its shape: fields lock, curation stays open.
  const locked = !!template && !template.parent_id && template.status !== 'DRAFT' && template.status !== 'REJECTED';
  const parent = useMemo(() => shapes.find((sh) => sh.id === parentId) || null, [shapes, parentId]);
  const parentBlanks = useMemo(() => blanksOf(parent?.body), [parent]);
  const parentHasMedia = !!parent && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(parent.template_type);
  const parentUrlButton = parent?.buttons?.find((b) => b.type === 'URL') || null;

  // Asset picker state
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [assetPickerContext, setAssetPickerContext] = useState<{
    type: 'header' | 'carousel';
    cardIndex?: number;
  }>({ type: 'header' });
  const [selectedHeaderAsset, setSelectedHeaderAsset] = useState<Asset | null>(null);
  const [carouselAssets, setCarouselAssets] = useState<{ [key: number]: Asset }>({});

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Detect variables from body text
  const detectedVariables = useMemo(() => detectVariables(body), [body]);

  // Update body variables when detected variables change
  useEffect(() => {
    setBodyVariables((prev) => {
      const newVars = [...prev];
      // Ensure we have slots for all detected variables
      while (newVars.length < detectedVariables.length) {
        newVars.push('');
      }
      return newVars.slice(0, Math.max(detectedVariables.length, 1));
    });
  }, [detectedVariables]);

  // Pre-fill form for edit mode
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDisplayName(template.display_name);
      setTemplateType(template.template_type);
      setTemplateScope(template.template_scope || 'OUTBOUND');
      setCategory(template.category);
      setLanguage(template.language);
      setHeaderText(template.header_text || '');
      setHeaderMediaUrl(template.header_media_url || '');
      setBody(template.body || '');
      setBodyVariables(template.body_variables || []);
      setFooter(template.footer || '');
      setButtons(template.buttons || []);
      setCarouselCards(template.carousel_cards || []);
      setTags(template.tags?.join(', ') || '');

      // Determine button type from existing buttons
      if (template.buttons && template.buttons.length > 0) {
        if (template.buttons[0].type === 'QUICK_REPLY') {
          setButtonType('quick_reply');
        } else {
          setButtonType('cta');
        }
      } else {
        setButtonType(null);
      }
    }
  }, [template]);

  // Handle template type change
  const handleTypeChange = (newType: WhatsAppTemplateType) => {
    setTemplateType(newType);

    // Clear irrelevant fields when switching types
    if (newType === 'TEXT' || newType === 'CAROUSEL') {
      setHeaderText('');
      setHeaderMediaUrl('');
      setSelectedHeaderAsset(null);
    }

    if (newType !== 'TEXT_HEADER') {
      setHeaderText('');
    }

    if (!['IMAGE', 'VIDEO', 'DOCUMENT'].includes(newType)) {
      setHeaderMediaUrl('');
      setSelectedHeaderAsset(null);
    }

    if (newType === 'CAROUSEL') {
      // Initialize with 2 empty cards for carousel
      if (carouselCards.length === 0) {
        setCarouselCards([
          {
            card_index: 0,
            header_format: 'IMAGE',
            header_media_url: '',
            title: '',
            body: '',
            body_variables: [],
            buttons: [],
          },
          {
            card_index: 1,
            header_format: 'IMAGE',
            header_media_url: '',
            title: '',
            body: '',
            body_variables: [],
            buttons: [],
          },
        ]);
      }
      // Clear main buttons for carousel (they have per-card buttons)
      setButtonType(null);
      setButtons([]);
    }
  };

  // Get asset type for picker
  const getAssetType = (): 'image' | 'video' | 'pdf' => {
    if (assetPickerContext.type === 'carousel') {
      return 'image'; // Carousel cards always use images
    }
    switch (mode === 'fill' && parent ? parent.template_type : templateType) {
      case 'IMAGE':
        return 'image';
      case 'VIDEO':
        return 'video';
      case 'DOCUMENT':
        return 'pdf';
      default:
        return 'image';
    }
  };

  // Handle asset selection
  const handleAssetSelect = (asset: Asset) => {
    if (assetPickerContext.type === 'header' && mode === 'fill') {
      setFillHeaderUrl(asset.url || '');
    } else if (assetPickerContext.type === 'header') {
      setSelectedHeaderAsset(asset);
      setHeaderMediaUrl(asset.url || '');
    } else if (assetPickerContext.type === 'carousel' && assetPickerContext.cardIndex !== undefined) {
      const cardIndex = assetPickerContext.cardIndex;
      setCarouselAssets((prev) => ({ ...prev, [cardIndex]: asset }));
      setCarouselCards((prev) => {
        const newCards = [...prev];
        newCards[cardIndex] = {
          ...newCards[cardIndex],
          header_media_url: asset.url || '',
        };
        return newCards;
      });
    }
    setShowAssetPicker(false);
  };

  // Open asset picker for header
  const openHeaderAssetPicker = () => {
    setAssetPickerContext({ type: 'header' });
    setShowAssetPicker(true);
  };

  // Open asset picker for carousel card
  const openCarouselAssetPicker = (cardIndex: number) => {
    setAssetPickerContext({ type: 'carousel', cardIndex });
    setShowAssetPicker(true);
  };

  // Add carousel card
  const addCarouselCard = () => {
    if (carouselCards.length < 10) {
      setCarouselCards((prev) => [
        ...prev,
        {
          card_index: prev.length,
          header_format: 'IMAGE',
          header_media_url: '',
          title: '',
          body: '',
          body_variables: [],
          buttons: [],
        },
      ]);
      setExpandedCardIndex(carouselCards.length);
    }
  };

  // Remove carousel card
  const removeCarouselCard = (index: number) => {
    if (carouselCards.length > 2) {
      setCarouselCards((prev) => {
        const newCards = prev.filter((_, i) => i !== index);
        // Reindex cards
        return newCards.map((card, i) => ({ ...card, card_index: i }));
      });
      // Also remove the asset reference
      setCarouselAssets((prev) => {
        const newAssets = { ...prev };
        delete newAssets[index];
        // Reindex assets
        const reindexed: { [key: number]: Asset } = {};
        Object.keys(newAssets).forEach((key) => {
          const oldIndex = parseInt(key);
          const newIndex = oldIndex > index ? oldIndex - 1 : oldIndex;
          reindexed[newIndex] = newAssets[oldIndex];
        });
        return reindexed;
      });
      if (expandedCardIndex >= carouselCards.length - 1) {
        setExpandedCardIndex(Math.max(0, carouselCards.length - 2));
      }
    }
  };

  // Update carousel card field
  const updateCarouselCard = (index: number, field: keyof CarouselCard, value: any) => {
    setCarouselCards((prev) => {
      const newCards = [...prev];
      newCards[index] = { ...newCards[index], [field]: value };
      return newCards;
    });
  };

  // Update carousel card buttons
  const updateCarouselCardButtons = (cardIndex: number, newButtons: WhatsAppButton[]) => {
    setCarouselCards((prev) => {
      const newCards = [...prev];
      newCards[cardIndex] = { ...newCards[cardIndex], buttons: newButtons };
      return newCards;
    });
  };

  // Validate form
  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (mode === 'fill') {
      if (!name.trim()) newErrors.name = 'Template name is required';
      else if (!/^[a-z0-9_]+$/.test(name)) newErrors.name = 'Name must be lowercase letters, numbers, and underscores only';
      if (!displayName.trim()) newErrors.displayName = 'Display name is required';
      if (!parent) newErrors.parent = 'Pick the shape this row fills';
      if (parentBlanks.some((n) => !(bodyParams[n - 1] || '').trim())) newErrors.params = 'Every blank needs a value';
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    // Name validation
    if (!name.trim()) {
      newErrors.name = 'Template name is required';
    } else if (!/^[a-z0-9_]+$/.test(name)) {
      newErrors.name = 'Name must be lowercase letters, numbers, and underscores only';
    }

    // Display name validation
    if (!displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }

    // Body validation
    if (!body.trim()) {
      newErrors.body = 'Message body is required';
    }

    // Header text validation for TEXT_HEADER
    if (templateType === 'TEXT_HEADER' && !headerText.trim()) {
      newErrors.headerText = 'Header text is required for text header templates';
    }

    // Header media validation for IMAGE/VIDEO/DOCUMENT
    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templateType) && !headerMediaUrl) {
      newErrors.headerMedia = 'Please select a media asset for the header';
    }

    // Carousel validation
    if (templateType === 'CAROUSEL') {
      if (carouselCards.length < 2) {
        newErrors.carousel = 'Carousel templates require at least 2 cards';
      } else {
        const missingImages = carouselCards.filter((card) => !card.header_media_url);
        if (missingImages.length > 0) {
          newErrors.carousel = `Cards ${missingImages.map((c) => c.card_index + 1).join(', ')} need images`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);

    try {
      const tagList = tags.split(',').map((t) => t.trim()).filter((t) => t !== '');
      const curation = { display_name: displayName.trim(), group: group.trim() || null, best_for: bestFor.trim() || null, tags: tagList };

      if (mode === 'fill') {
        const params: TemplateParams = { body_params: parentBlanks.map((n) => (bodyParams[n - 1] || '').trim()) };
        if (fillHeaderUrl) params.header_media_url = fillHeaderUrl;
        if (linkParams.trim()) params.link_params = linkParams.trim();
        if (template) await patchWhatsAppTemplate(template.id, { ...curation, params });
        else await createWhatsAppTemplate({ name: name.trim(), parent_id: parentId, params, ...curation });
        onSuccess();
        return;
      }

      if (locked) {
        // The shape is Meta's now; only what we own goes out.
        await patchWhatsAppTemplate(template!.id, { ...curation, ...(headerMediaUrl ? { header_media_url: headerMediaUrl } : {}) });
        onSuccess();
        return;
      }

      // Build the template create object
      const templateData: WhatsAppTemplateCreate = {
        name: name.trim(),
        display_name: displayName.trim(),
        language,
        category,
        template_type: templateType,
        template_scope: templateScope,
        header_format: getHeaderFormatForType(templateType),
        body: body.trim(),
        body_variables: bodyVariables.filter((v) => v.trim() !== ''),
        tags: tagList,
        group: curation.group,
        best_for: curation.best_for,
      };

      // Add header based on type
      if (templateType === 'TEXT_HEADER' && headerText) {
        templateData.header_text = headerText.trim();
      }

      if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templateType) && headerMediaUrl) {
        templateData.header_media_url = headerMediaUrl;
      }

      // Add footer if present
      if (footer.trim()) {
        templateData.footer = footer.trim();
      }

      // Add buttons for non-carousel types
      if (templateType !== 'CAROUSEL' && buttons.length > 0) {
        templateData.button_type = buttonType || undefined;
        templateData.buttons = buttons.filter((b) => b.text.trim() !== '');
      }

      // Add carousel cards
      if (templateType === 'CAROUSEL') {
        templateData.carousel_cards = carouselCards.map((card, index) => ({
          ...card,
          card_index: index,
          body_variables: card.body_variables || [],
          buttons: card.buttons?.filter((b) => b.text.trim() !== '') || [],
        }));
      }

      // Create or update
      if (template) {
        await updateWhatsAppTemplate(template.id, templateData);
      } else {
        await createWhatsAppTemplate(templateData);
      }

      onSuccess();
    } catch (error: any) {
      console.error('Error saving template:', error);
      const message = error.response?.data?.detail || 'Failed to save template';
      setErrors({ submit: typeof message === 'string' ? message : JSON.stringify(message) });
    }

    setSubmitting(false);
  };

  // The other verbs an existing row has. None of them sends a message.
  const act = async (verb: 'submit' | 'retire' | 'delete' | 'test') => {
    if (!template) return;
    if (verb === 'retire' && !confirmRetire) { setConfirmRetire(true); return; }
    setSubmitting(true);
    try {
      if (verb === 'test') {
        // One real send to the owner's number, on the ledger as a test.
        const r = await testSend(template.id);
        setTested(`test queued → ${r.to}`);
        setSubmitting(false);
        return;
      }
      if (verb === 'submit') await submitWhatsAppTemplate(template.id);
      if (verb === 'retire') await patchWhatsAppTemplate(template.id, { retired_at: new Date().toISOString() });
      if (verb === 'delete') await deleteWhatsAppTemplate(template.id);
      onSuccess();
    } catch (error: any) {
      const message = error.response?.data?.detail || `Failed to ${verb}`;
      setErrors({ submit: typeof message === 'string' ? message : JSON.stringify(message) });
      setSubmitting(false);
    }
  };

  const preview = mode === 'fill' && parent
    ? render({ ...(template || ({} as WhatsAppTemplate)), parent_id: parent.id, params: { body_params: bodyParams, header_media_url: fillHeaderUrl || undefined } }, parent)
    : { templateType, headerText, headerMediaUrl, body, bodyVariables, footer, buttons, carouselCards };
  // Handle body variable change
  const handleBodyVariableChange = (index: number, value: string) => {
    setBodyVariables((prev) => {
      const newVars = [...prev];
      newVars[index] = value;
      return newVars;
    });
  };

  const fieldErrors = Object.keys(errors).filter((k) => k !== 'submit').length;
  const primaryLabel = submitting ? 'Saving…' : template ? 'Save' : mode === 'fill' ? 'Create message' : 'Save draft';
  const mediaKind = (t: WhatsAppTemplateType) => (t === 'IMAGE' ? 'image' : t === 'VIDEO' ? 'video' : 'document');

  return (
    <>
      <Modal
        wide
        onClose={onClose}
        title={
          <span className="flex items-center gap-2.5 flex-wrap">
            {template ? template.display_name : 'New template'}
            {template
              ? <Pill tone={statusTone(template.status)}>{template.status.toLowerCase()} at Meta{template.parent_id ? ' · inherited' : ''}</Pill>
              : <Pill>new · unsaved</Pill>}
            {locked && <span className="font-sans text-[12px] font-semibold text-amber-ink">shape locked · approved at Meta</span>}
          </span>
        }
        footer={<>
          <span className="flex-1 min-w-0 text-[12.5px]">
            {errors.submit
              ? <span className="text-brick font-semibold">{errors.submit}</span>
              : fieldErrors > 0
                ? <span className="text-brick font-semibold">Fix {fieldErrors} field{fieldErrors === 1 ? '' : 's'} above</span>
                : template && <span className="text-graphite">saved {new Date(template.updated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</span>}
          </span>
          {template && template.status === 'DRAFT' && (
            <button type="button" onClick={() => act('delete')} disabled={submitting} className={`${btn} ${btnTone.brick}`}>Delete draft</button>
          )}
          {template && template.status === 'APPROVED' && (
            <button type="button" onClick={() => act('retire')} disabled={submitting}
                    className={`${btn} ${confirmRetire ? 'bg-brick text-warm-white hover:bg-brick/90' : btnTone.brick}`}>
              {confirmRetire ? 'Retire — nothing can pick it again' : 'Retire'}
            </button>
          )}
          {template && (
            <button type="button" onClick={() => act('test')} disabled={submitting} className={`${btn} ${btnTone.charcoal}`}>
              {tested || 'Send me a test'}
            </button>
          )}
          {template && !template.parent_id && template.status === 'DRAFT' && template.template_scope === 'OUTBOUND' && (
            <button type="button" onClick={() => act('submit')} disabled={submitting} className={`${btn} ${btnTone.charcoal}`}>Submit to Meta</button>
          )}
          {template && !template.parent_id && template.status === 'DRAFT' && template.template_scope === 'IN_SESSION' && (
            <button type="button" onClick={() => act('submit')} disabled={submitting} className={`${btn} ${btnTone.charcoal}`}>Make live</button>
          )}
          <button type="button" onClick={onClose} className={`${btn} ${btnTone.ghost}`}>Cancel</button>
          <button type="button" onClick={handleSubmit} disabled={submitting} className={`${btn} ${btnTone.forest} px-5`}>{primaryLabel}</button>
        </>}
      >
        <div className="grid grid-cols-[minmax(0,1fr)_320px] gap-10">
          <div className="space-y-8 min-w-0">
            {!template && (
              <Tabs value={mode} onChange={setMode} items={[['shape', 'A new shape'], ['fill', 'A shape I already have']]} />
            )}

            {mode === 'fill' && (
              <>
                <Group title="The shape it fills">
                  <Field label="Approved shape" required error={errors.parent}
                         hint={parent && <>Goes out as <b className="text-charcoal">{parent.name}</b> with the blanks filled in; its Meta status is the shape's.</>}>
                    <select value={parentId} onChange={(e) => { setParentId(e.target.value); setBodyParams([]); setFillHeaderUrl(''); }} disabled={!!template}
                            className={fieldCls(errors.parent)}>
                      <option value="">choose a shape…</option>
                      {shapes.filter((sh) => sh.status === 'APPROVED' && !sh.retired_at).map((sh) => (
                        <option key={sh.id} value={sh.id}>{sh.display_name} · {sh.name}</option>
                      ))}
                    </select>
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Template name" required error={errors.name}>
                      <input type="text" value={name} disabled={!!template}
                             onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                             placeholder={parent ? `${parent.name}_oct_2026` : 'lowercase_with_underscores'} className={fieldCls(errors.name)} />
                    </Field>
                    <Field label="Display name" required error={errors.displayName}>
                      <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="How it reads in lists" className={fieldCls(errors.displayName)} />
                    </Field>
                  </div>
                </Group>

                {parent && (
                  <Group title="The blanks">
                    {parentBlanks.length === 0 && !parentHasMedia && !parentUrlButton && (
                      <p className="text-graphite text-[13px]">Nothing to fill — it can be sent as it is.</p>
                    )}
                    {parentBlanks.map((n) => (
                      <Field key={n} label={`Value for {{${n}}}`} required>
                        <input type="text" value={bodyParams[n - 1] || ''} placeholder="one line"
                               onChange={(e) => setBodyParams((prev) => { const next = [...prev]; while (next.length < n) next.push(''); next[n - 1] = e.target.value; return next; })}
                               className={fieldCls(errors.params && !(bodyParams[n - 1] || '').trim() ? errors.params : undefined)} />
                      </Field>
                    ))}
                    {errors.params && <ErrorLine>{errors.params}</ErrorLine>}
                    {parentHasMedia && (
                      <Field label={`Header ${mediaKind(parent.template_type)} · this message`} hint="A changed file needs a new filename — WhatsApp caches by URL.">
                        <MediaRow
                          url={fillHeaderUrl}
                          video={parent.template_type === 'VIDEO'}
                          doc={parent.template_type === 'DOCUMENT'}
                          name={fillHeaderUrl ? fillHeaderUrl.split('/').pop() : undefined}
                          empty={`the shape's own ${mediaKind(parent.template_type)} goes out`}
                          onPick={openHeaderAssetPicker}
                          onClear={fillHeaderUrl ? () => setFillHeaderUrl('') : undefined}
                          clearLabel="use the shape's"
                        />
                      </Field>
                    )}
                    {parentUrlButton && (
                      <Field label={`Link tail · after "${parentUrlButton.text}"`} hint={<>Appended to {parentUrlButton.url}</>}>
                        <input type="text" value={linkParams} onChange={(e) => setLinkParams(e.target.value)} placeholder="f=v07&src=album" className={fieldCls()} />
                      </Field>
                    )}
                  </Group>
                )}
              </>
            )}

            {mode === 'shape' && (<>
              <fieldset disabled={locked} className="space-y-8 min-w-0 border-0 p-0 m-0">
                <Group title="Basics">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Template name" required error={errors.name} hint="lowercase, numbers, underscores">
                      <input type="text" value={name}
                             onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                             placeholder="my_template_name" className={fieldCls(errors.name)} />
                    </Field>
                    <Field label="Display name" required error={errors.displayName}>
                      <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                             placeholder="How it reads in lists" className={fieldCls(errors.displayName)} />
                    </Field>
                    <Field label="Category">
                      <select value={category} onChange={(e) => setCategory(e.target.value as WhatsAppTemplateCategory)} className={fieldCls()}>
                        {CATEGORIES.map((cat) => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Language">
                      <select value={language} onChange={(e) => setLanguage(e.target.value)} className={fieldCls()}>
                        {LANGUAGES.map((lang) => <option key={lang.value} value={lang.value}>{lang.label}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Type">
                    <Tabs value={templateType} onChange={handleTypeChange}
                          items={TEMPLATE_TYPES.map((t): [WhatsAppTemplateType, React.ReactNode] => [t.value, t.label])} />
                  </Field>
                  <Field label="Scope" hint={templateScope === 'IN_SESSION' ? 'Replies inside an open conversation' : 'Starts a conversation · needs Meta approval'}>
                    <Tabs value={templateScope} onChange={setTemplateScope} items={[['IN_SESSION', 'In-session'], ['OUTBOUND', 'Outbound']]} />
                  </Field>
                </Group>

                {templateType === 'TEXT_HEADER' && (
                  <Group title="Header">
                    <Field label="Header text" required error={errors.headerText} hint={'Supports {{1}}'} count={`${headerText.length}/60`}>
                      <input type="text" value={headerText} onChange={(e) => setHeaderText(e.target.value)} maxLength={60}
                             placeholder="Your header text" className={fieldCls(errors.headerText)} />
                    </Field>
                  </Group>
                )}
              </fieldset>

              {/* Stays editable on a locked shape: Meta approved the format, not the file */}
              {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templateType) && (
                <Group title="Header media" note={locked ? 'editable after approval' : undefined}>
                  <Field label={mediaKind(templateType)} required error={errors.headerMedia}>
                    <MediaRow
                      url={headerMediaUrl}
                      video={templateType === 'VIDEO'}
                      doc={templateType === 'DOCUMENT'}
                      name={selectedHeaderAsset?.name || (headerMediaUrl ? headerMediaUrl.split('/').pop() : undefined)}
                      empty={`no ${mediaKind(templateType)} chosen`}
                      onPick={openHeaderAssetPicker}
                      invalid={!!errors.headerMedia}
                    />
                  </Field>
                </Group>
              )}

              <fieldset disabled={locked} className="space-y-8 min-w-0 border-0 p-0 m-0">
                <Group title="Message">
                  <Field label="Body" required error={errors.body} hint={'{{1}}, {{2}} for blanks'} count={`${body.length}/1024`}>
                    <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={1024} rows={6}
                              placeholder="What the lead reads"
                              className={`${fieldCls(errors.body)} !h-auto py-2 leading-relaxed resize-y`} />
                  </Field>
                  {detectedVariables.length > 0 && (
                    <Field label="Sample values · preview only">
                      <div className="space-y-2">
                        {detectedVariables.map((varNum, index) => (
                          <div key={varNum} className="flex items-center gap-3">
                            <span className="w-12 text-[12.5px] font-semibold text-forest num">{`{{${varNum}}}`}</span>
                            <input type="text" value={bodyVariables[index] || ''} onChange={(e) => handleBodyVariableChange(index, e.target.value)}
                                   placeholder={`Sample for {{${varNum}}}`} className={fieldCls()} />
                          </div>
                        ))}
                      </div>
                    </Field>
                  )}
                  <Field label="Footer" count={`${footer.length}/60`}>
                    <input type="text" value={footer} onChange={(e) => setFooter(e.target.value)} maxLength={60}
                           placeholder="optional · e.g. Reply STOP to unsubscribe" className={fieldCls()} />
                  </Field>
                </Group>

                {templateType === 'CAROUSEL' && (
                  <Group title="Cards" note={`${carouselCards.length}/10`}>
                    {errors.carousel && <ErrorLine>{errors.carousel}</ErrorLine>}
                    <div>
                      {carouselCards.map((card, index) => {
                        const open = expandedCardIndex === index;
                        return (
                          <div key={index} className="border-b border-charcoal/10 last:border-b-0">
                            <div role="button" tabIndex={0}
                                 onClick={() => setExpandedCardIndex(open ? -1 : index)}
                                 onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedCardIndex(open ? -1 : index); } }}
                                 className="flex items-center gap-3 py-2.5 px-1 cursor-pointer hover:bg-charcoal/[0.025] rounded-md">
                              <Thumb src={card.header_media_url || null} className="w-12 h-9" />
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-[13.5px]">Card {index + 1}</div>
                                <div className={`text-[12px] truncate ${card.header_media_url ? 'text-graphite' : 'text-brick'}`}>
                                  {card.header_media_url ? (card.title || 'no title') : 'needs an image'}
                                </div>
                              </div>
                              {carouselCards.length > 2 && (
                                <button type="button" aria-label={`Remove card ${index + 1}`}
                                        onClick={(e) => { e.stopPropagation(); removeCarouselCard(index); }}
                                        className="w-8 h-8 grid place-items-center rounded-lg text-graphite hover:text-brick hover:bg-brick/5">✕</button>
                              )}
                              <span className={`text-graphite transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden>›</span>
                            </div>
                            {open && (
                              <div className="pl-[60px] pr-1 pb-5 pt-1 space-y-4">
                                <Field label="Image" required>
                                  <MediaRow url={card.header_media_url || ''} name={carouselAssets[index]?.name}
                                            empty="no image chosen" onPick={() => openCarouselAssetPicker(index)} invalid={!card.header_media_url && !!errors.carousel} />
                                </Field>
                                <Field label="Title" count={`${(card.title || '').length}/60`}>
                                  <input type="text" value={card.title || ''} onChange={(e) => updateCarouselCard(index, 'title', e.target.value)}
                                         maxLength={60} placeholder="optional" className={fieldCls()} />
                                </Field>
                                <Field label="Body" count={`${(card.body || '').length}/160`}>
                                  <textarea value={card.body || ''} onChange={(e) => updateCarouselCard(index, 'body', e.target.value)}
                                            maxLength={160} rows={2} placeholder="optional"
                                            className={`${fieldCls()} !h-auto py-2 resize-none`} />
                                </Field>
                                <Field label="Buttons">
                                  <ButtonEditor
                                    buttonType={card.buttons && card.buttons.length > 0 ? (card.buttons[0].type === 'QUICK_REPLY' ? 'quick_reply' : 'cta') : null}
                                    buttons={card.buttons || []}
                                    onChange={(newButtons) => updateCarouselCardButtons(index, newButtons)}
                                    onButtonTypeChange={(type) => {
                                      if (type === null) {
                                        updateCarouselCardButtons(index, []);
                                      } else if (type === 'quick_reply') {
                                        updateCarouselCardButtons(index, [{ type: 'QUICK_REPLY', text: '' }]);
                                      } else {
                                        updateCarouselCardButtons(index, [{ type: 'PHONE_NUMBER', text: '' }]);
                                      }
                                    }}
                                    maxButtons={2}
                                    showTypeToggle={true}
                                  />
                                </Field>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {carouselCards.length < 10 && (
                      <button type="button" onClick={addCarouselCard} className={`${btn} ${btnTone.ghost}`}>+ Add card</button>
                    )}
                  </Group>
                )}

                {templateType !== 'CAROUSEL' && (
                  <Group title="Buttons">
                    <ButtonEditor
                      buttonType={buttonType}
                      buttons={buttons}
                      onChange={setButtons}
                      onButtonTypeChange={setButtonType}
                      maxButtons={3}
                      showTypeToggle={true}
                    />
                  </Group>
                )}
              </fieldset>
            </>)}

            {/* Curation — what the list, the pickers and the numbers see; always editable */}
            <Group title="Listing" note={locked ? 'editable after approval' : undefined}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Group">
                  <input type="text" list="template-groups" value={group} onChange={(e) => setGroup(e.target.value)} placeholder="e.g. Events, Proof, Visit" className={fieldCls()} />
                  <datalist id="template-groups">
                    {Array.from(new Set(shapes.map((sh) => sh.group).filter(Boolean))).map((g) => <option key={g!} value={g!} />)}
                  </datalist>
                </Field>
                <Field label="Best for">
                  <input type="text" value={bestFor} onChange={(e) => setBestFor(e.target.value)} placeholder="e.g. joint families, silent 2+ weeks" className={fieldCls()} />
                </Field>
              </div>
              <Field label="Tags" hint="comma-separated">
                <input type="text" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="promo, sale, diwali" className={fieldCls()} />
              </Field>
            </Group>
          </div>

          <aside className="sticky top-0 self-start">
            <div className={`${label} !mb-2`}>Preview · as the lead sees it</div>
            <div className="rounded-lg bg-charcoal/[0.04] p-4">
              <WhatsAppBubble r={preview} />
            </div>
          </aside>
        </div>
      </Modal>

      <AssetPickerModal
        isOpen={showAssetPicker}
        assetType={getAssetType()}
        onSelect={handleAssetSelect}
        onClose={() => setShowAssetPicker(false)}
      />
    </>
  );
};

/** Form building blocks — local to this form. */
const fieldCls = (error?: string) =>
  `${input} w-full disabled:bg-charcoal/[0.04] disabled:text-graphite disabled:cursor-not-allowed ${error ? '!border-brick focus:!ring-brick/20' : ''}`;

const Group: React.FC<{ title: string; note?: React.ReactNode; children: React.ReactNode }> = ({ title, note, children }) => (
  <section>
    <div className="flex items-end justify-between gap-3 pb-2 mb-4 border-b border-charcoal/15">
      <h3 className="font-display text-[16px] font-semibold tracking-tight">{title}</h3>
      {note && <span className="text-graphite text-xs">{note}</span>}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const ErrorLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-brick text-[12.5px] font-semibold" role="alert">{children}</p>
);

const Field: React.FC<{
  label: React.ReactNode;
  required?: boolean;
  hint?: React.ReactNode;
  count?: string;
  error?: string;
  children: React.ReactNode;
}> = ({ label: text, required, hint, count, error, children }) => (
  <div className="min-w-0">
    <div className={label}>{text}{required && <span className="text-brick ml-0.5">*</span>}</div>
    {children}
    {(error || hint || count) && (
      <div className="flex justify-between gap-3 mt-1 text-[11.5px]">
        {error ? <span className="text-brick font-semibold" role="alert">{error}</span> : <span className="text-graphite">{hint}</span>}
        {count && <span className="text-graphite num flex-none">{count}</span>}
      </div>
    )}
  </div>
);

/** A chosen media file as one row — thumbnail, name, change — or a pick button. */
const MediaRow: React.FC<{
  url: string;
  video?: boolean;
  doc?: boolean;
  name?: string;
  empty: string;
  onPick: () => void;
  onClear?: () => void;
  clearLabel?: string;
  invalid?: boolean;
}> = ({ url, video, doc, name, empty, onPick, onClear, clearLabel, invalid }) => (
  <div className="flex items-center gap-3">
    {url && !doc && <Thumb src={url} video={video} />}
    {url && doc && <span className="w-16 h-11 flex-none grid place-items-center rounded-md bg-charcoal/[0.06] text-graphite text-[11px] font-semibold">PDF</span>}
    <span className={`flex-1 min-w-0 truncate text-[13px] ${url ? 'text-charcoal' : invalid ? 'text-brick' : 'text-graphite'}`} title={url || undefined}>
      {url ? (name || url) : empty}
    </span>
    {onClear && <button type="button" onClick={onClear} className={`${btn} ${btnTone.ghost}`}>{clearLabel}</button>}
    <button type="button" onClick={onPick} className={`${btn} ${invalid && !url ? btnTone.brick : btnTone.charcoal}`}>{url ? 'Change' : 'Choose from assets'}</button>
  </div>
);

export default TemplateForm;
